import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  BellRing,
  RefreshCw,
  Send,
  CheckCircle2,
  XCircle,
  AlertCircle,
  BellOff,
  MailWarning,
  UserX,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  ok: {
    label: "Subscribed",
    Icon: CheckCircle2,
    className: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  },
  not_subscribed: {
    label: "Never enabled",
    Icon: BellOff,
    className: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  },
  disabled: {
    label: "Turned off",
    Icon: BellOff,
    className: "border-orange-500/40 text-orange-300 bg-orange-500/10",
  },
  no_account: {
    label: "No user account",
    Icon: UserX,
    className: "border-red-500/40 text-red-300 bg-red-500/10",
  },
  no_email: {
    label: "No email on file",
    Icon: MailWarning,
    className: "border-red-500/40 text-red-300 bg-red-500/10",
  },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || {
    label: status,
    Icon: AlertCircle,
    className: "border-muted-foreground/40 text-muted-foreground",
  };
  const { label, Icon, className } = meta;
  return (
    <Badge
      variant="outline"
      className={`${className} px-2 py-0.5`}
      data-testid={`push-status-${status}`}
    >
      <Icon className="w-3.5 h-3.5 mr-1.5" />
      {label}
    </Badge>
  );
};

const PushDiagnostic = () => {
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState({}); // user_id -> boolean

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([
        apiClient.get("/admin/push-diagnostic/summary"),
        apiClient.get("/admin/push-diagnostic/members"),
      ]);
      setSummary(s.data);
      setMembers(m.data.members || []);
    } catch (e) {
      toast.error(`Failed to load diagnostic: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const sendTest = async (row) => {
    if (!row.user_id) return;
    setPinging((p) => ({ ...p, [row.user_id]: true }));
    try {
      const { data } = await apiClient.post(`/admin/push-diagnostic/test/${row.user_id}`);
      if (data.ok) {
        toast.success(`Sent to ${row.name}: ${data.detail}`);
      } else {
        toast.error(`${row.name}: ${data.detail}`, { duration: 6000 });
      }
    } catch (e) {
      toast.error(`${row.name}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPinging((p) => ({ ...p, [row.user_id]: false }));
    }
  };

  const stats = summary
    ? [
        { label: "Total members", value: summary.total_members, tone: "text-foreground" },
        { label: "Subscribed & on", value: summary.subscribed_and_enabled, tone: "text-emerald-400" },
        { label: "Never enabled", value: summary.no_push_subscription, tone: "text-amber-400" },
        { label: "No user account", value: summary.no_user_account, tone: "text-red-400" },
      ]
    : [];

  return (
    <div className="space-y-6" data-testid="push-diagnostic-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl flex items-center gap-2">
            <BellRing className="w-6 h-6 text-primary" />
            Push Notification Diagnostic
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Push only reaches members who've clicked "Enable push notifications" on
            their own Profile from their own device. Use this page to see who's
            missing and fire a diagnostic push at anyone who should be receiving them.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          disabled={loading}
          data-testid="push-diagnostic-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <Card key={s.label} className="glass-card">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {s.label}
              </p>
              <p className={`text-3xl font-heading mt-1 ${s.tone}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {summary?.by_provider && Object.keys(summary.by_provider).length > 0 && (
        <Card className="glass-card">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Subscribed by browser vendor
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.by_provider).map(([prov, n]) => (
                <Badge key={prov} variant="outline" className="border-white/20">
                  {prov}: {n}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="glass-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="push-diagnostic-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-white/5">
                  <th className="px-4 py-3">Member</th>
                  <th className="px-4 py-3 hidden md:table-cell">Section</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Test push</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 && !loading && (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">
                      No members found.
                    </td>
                  </tr>
                )}
                {members.map((m) => (
                  <tr
                    key={m.player_id}
                    className="border-b border-white/5 last:border-b-0"
                    data-testid={`push-row-${m.player_id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">{m.name || "—"}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[220px]">
                        {m.email || "no email"}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">
                      {m.section || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={m.status} />
                      {m.provider && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          via {m.provider}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={m.status !== "ok" || pinging[m.user_id]}
                        onClick={() => sendTest(m)}
                        data-testid={`push-test-${m.player_id}`}
                      >
                        {pinging[m.user_id] ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-3.5 h-3.5 mr-1.5" />
                            Test
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p>
          <strong>How push actually works:</strong> each member has to visit their
          Profile from their own phone/browser and click <em>Enable push notifications</em>.
          A member who's never done this shows as <em>Never enabled</em>, no matter how
          many events they've RSVP'd to. Firefox and iOS users must also install the
          PWA to Home Screen before push works. When someone clears browser data or
          switches browsers, they'll need to enable again.
        </p>
      </div>
    </div>
  );
};

export default PushDiagnostic;
